/**
 * RefundForm Component - Form for creating refunds
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { createRefund } from "../services/refundService.js";
import { logError } from "../../../shared/utils/logger.js";

/**
 * RefundForm Component
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.invoice - Invoice object
 * @param {Function} props.onRefundCreated - Callback after refund creation
 */
export const RefundForm = ({ open, onClose, invoice, onRefundCreated }) => {
  const [refundData, setRefundData] = useState({
    amount: "",
    reason: "",
    classification: "payment_reversal",
    method: "bank_transfer",
    reference: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Calculate available refundable amount
  const totalPaid =
    (invoice?.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0) ||
    0;
  const totalRefunded =
    (invoice?.refunds || [])
      .filter((r) => r.status !== "cancelled")
      .reduce((sum, r) => sum + (r.amount || 0), 0) || 0;
  const availableForRefund = Math.max(totalPaid - totalRefunded, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(amount || 0);
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!refundData.amount || parseFloat(refundData.amount) <= 0) {
      setError("Refund amount must be greater than 0");
      return;
    }

    const amount = parseFloat(refundData.amount);
    if (amount > availableForRefund) {
      setError(
        `Refund amount cannot exceed available refundable amount of ${formatCurrency(
          availableForRefund
        )}`
      );
      return;
    }

    if (!refundData.reason || refundData.reason.trim().length < 3) {
      setError("Refund reason is required (minimum 3 characters)");
      return;
    }

    try {
      setLoading(true);
      
      // Get jobId from invoice (required by backend)
      const jobId = invoice?.jobId;
      if (!jobId) {
        setError("Invoice jobId is required. Please ensure the invoice is linked to a job.");
        setLoading(false);
        return;
      }
      
      const result = await createRefund(invoice.id, {
        amount: amount,
        reason: refundData.reason.trim(),
        classification: refundData.classification || "payment_reversal",
        method: refundData.method,
        reference: refundData.reference.trim() || null,
        notes: refundData.notes.trim() || null,
        jobId: jobId,
      });

      if (result.success) {
        // Reset form
        setRefundData({
          amount: "",
          reason: "",
          classification: "payment_reversal",
          method: "bank_transfer",
          reference: "",
          notes: "",
        });
        onRefundCreated?.();
        onClose();
      } else {
        setError(result.error || "Failed to create refund");
      }
    } catch (err) {
      setError(err.message || "Failed to create refund");
      logError("Refund form error", { error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setRefundData({
        amount: "",
        reason: "",
        classification: "payment_reversal",
        method: "bank_transfer",
        reference: "",
        notes: "",
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Refund</DialogTitle>
      <DialogContent>
        {availableForRefund <= 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No refundable amount available. Total paid: {formatCurrency(totalPaid)}, Total refunded: {formatCurrency(totalRefunded)}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Available for refund: <strong>{formatCurrency(availableForRefund)}</strong>
          </Typography>
          {invoice?.jobId && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Job ID: <strong>{invoice.jobId}</strong>
            </Typography>
          )}
        </Box>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Refund Amount *"
              type="number"
              value={refundData.amount}
              onChange={(e) =>
                setRefundData({ ...refundData, amount: e.target.value })
              }
              required
              inputProps={{
                min: 0.01,
                max: availableForRefund,
                step: 0.01,
              }}
              helperText={`Maximum: ${formatCurrency(availableForRefund)}`}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reason *"
              value={refundData.reason}
              onChange={(e) =>
                setRefundData({ ...refundData, reason: e.target.value })
              }
              required
              multiline
              rows={3}
              helperText="Minimum 3 characters"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Refund Type"
              value={refundData.classification}
              onChange={(e) =>
                setRefundData({ ...refundData, classification: e.target.value })
              }
              helperText="Payment reversal affects invoice balance; compensation/overpayment do not"
            >
              <MenuItem value="payment_reversal">Payment Reversal</MenuItem>
              <MenuItem value="compensation">Compensation</MenuItem>
              <MenuItem value="overpayment_return">Overpayment Return</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Refund Method"
              value={refundData.method}
              onChange={(e) =>
                setRefundData({ ...refundData, method: e.target.value })
              }
            >
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="credit_note">Credit Note</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reference"
              value={refundData.reference}
              onChange={(e) =>
                setRefundData({ ...refundData, reference: e.target.value })
              }
              helperText="Optional reference number or transaction ID"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={refundData.notes}
              onChange={(e) =>
                setRefundData({ ...refundData, notes: e.target.value })
              }
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !refundData.amount ||
            !refundData.reason ||
            availableForRefund <= 0
          }
        >
          {loading ? <CircularProgress size={20} /> : "Create Refund"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundForm;

